// Un filtro colgado de la CABECERA de su columna.
//
// POR QUÉ ESTÁ AQUÍ Y NO EN LA BARRA DE ARRIBA
// --------------------------------------------
// La barra llegó a catorce controles y ninguno decía a qué columna miraba: hay
// que leer el rótulo del campo, buscar la columna que se le parece y confiar.
// Colgado de su propia cabecera, el filtro no necesita rótulo —la columna ya lo
// lleva— y no se puede confundir de campo.
//
// LO QUE NO SE MUEVE. Un filtro SIN columna (artículo, empresa) se queda en la
// barra: colgarlo de una cabecera cualquiera sería mentir sobre lo que filtra.
// Y ninguno vive en los dos sitios a la vez — dos puertas al mismo estado son
// dos sitios donde descuadrarlo.
//
// LA CABECERA TIENE QUE SER FIJA. Con los filtros aquí dentro, una cabecera que
// se va al desplazar deja la pantalla sin forma de filtrar. Por eso la tabla
// pasó a tener el alto acotado y `sticky top-0`, como ya hacía `ReportTable`.
//
// UNA COLUMNA PUEDE OFRECER DOS COSAS. «Cliente / Pedido» pinta las dos, así
// que su cabecera da las dos en pestañas. Partirla en dos columnas para tener
// dos cabeceras costaría 200 px de ancho en una tabla que ya se sale.
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { usePosicionOverlay } from './usePosicionOverlay'
import { CamposRango } from './CamposRango'
import { partirTerminos, resolverPegado } from '@/utils/filtroTexto'
import {
  activosDe, limpiarFiltro, type FiltroColumna, type SeccionFiltro, type SeccionLista,
} from '@/utils/filtroColumna'

/** A partir de cuántas opciones el panel trae su propio buscador. Por debajo
 *  estorba: se leen las cuatro de un vistazo antes de escribir nada. */
const UMBRAL_BUSCADOR = 8

/** Tope de opciones pintadas a la vez; el resto se resume. Mismo motivo que en
 *  `MultiFiltro`: nadie recorre mil opciones con la vista, y montarlas en cada
 *  pulsación deja el campo pensando mientras alguien escribe. */
const MAX_SUGERENCIAS = 200

const IcoEmbudo = () => (
  <svg
    className="w-2.5 h-2.5 shrink-0"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinejoin="round"
  >
    <path d="M2.2 3h11.6l-4.4 5.1v4.6l-2.8 1.5V8.1L2.2 3z" />
  </svg>
)

const IcoLupa = () => (
  <svg className="w-3 h-3 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
    <circle cx="7" cy="7" r="4.5" />
    <path d="M10.5 10.5L14 14" />
  </svg>
)

const IcoCheck = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 8.5l3.5 3.5L13 5" />
  </svg>
)

interface Props {
  dark: boolean
  /** El rótulo de la columna, YA traducido: esto no sabe de claves i18n. */
  etiqueta: string
  filtro: FiltroColumna
}

// El embudo va SIEMPRE detrás del rótulo, también en las columnas alineadas a
// la derecha. Nació invirtiendo el orden en ésas —para que el icono quedara
// hacia dentro— y el resultado fue que Reserva y Picking tenían el embudo
// delante del nombre y las demás detrás: la cabecera se leía distinta según la
// columna. El `text-right` del `th` ya empuja el botón entero a su sitio.
export function CabeceraFiltrable({ dark, etiqueta, filtro }: Props) {
  const { t } = useTranslation()
  const [abierto, setAbierto] = useState(false)
  const [pestana, setPestana] = useState(0)
  const [texto, setTexto] = useState('')
  const boton = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const pos = usePosicionOverlay(boton, abierto, { anchoMaximo: 280, altoDeseado: 360 })

  const activos = activosDe(filtro)
  const indice = Math.min(pestana, filtro.length - 1)
  const seccion: SeccionFiltro | undefined = filtro[indice]

  // El catálogo de la pestaña abierta. Se saca aquí, fuera del `useMemo`, para
  // que el hook no cuelgue de una rama: las listas y el rango son la misma
  // columna y los hooks tienen que correr siempre en el mismo orden.
  const opciones = seccion?.tipo === 'lista' ? seccion.opciones : []

  const coincidencias = useMemo(() => {
    const q = texto.trim().toLowerCase()
    if (!q) return opciones
    // Por el título —lo que se ve— y también por el valor, que puede no
    // coincidir: en artículos el título va formateado y el valor es el código
    // crudo. Sin mirar el valor, quien pega la referencia tal cual sale de D365
    // no encontraría su propia opción.
    return opciones.filter(o =>
      o.titulo.toLowerCase().includes(q)
      || o.valor.toLowerCase().includes(q)
      || (o.detalle ?? '').toLowerCase().includes(q))
  }, [opciones, texto])

  const visibles = useMemo(() => coincidencias.slice(0, MAX_SUGERENCIAS), [coincidencias])
  const ocultas = coincidencias.length - visibles.length

  useEffect(() => {
    if (abierto) return
    setTexto('')
    setPestana(0)
  }, [abierto])

  useEffect(() => {
    if (!abierto) return
    const fuera = (e: MouseEvent) => {
      const n = e.target as Node
      // El panel vive en `document.body`, fuera del árbol de este componente:
      // sin mirarlo aparte, pulsar una opción lo cerraría antes del clic.
      if (boton.current?.contains(n) || panel.current?.contains(n)) return
      setAbierto(false)
    }
    const escape = (e: KeyboardEvent) => { if (e.key === 'Escape') setAbierto(false) }
    document.addEventListener('mousedown', fuera)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', fuera)
      document.removeEventListener('keydown', escape)
    }
  }, [abierto])

  if (!seccion) return <span className="truncate">{etiqueta}</span>

  function alternar(s: SeccionLista, valor: string) {
    // Volver a pulsar lo ya elegido lo quita: es lo que espera quien lo ve
    // marcado, y así nunca hay duplicados.
    if (s.unico) {
      s.onSeleccion(s.seleccion.includes(valor) ? [] : [valor])
      return
    }
    s.onSeleccion(s.seleccion.includes(valor)
      ? s.seleccion.filter(v => v !== valor)
      : [...s.seleccion, valor])
  }

  // Pegar una columna de Excel añade un término por línea. El `input` es de una
  // sola línea y el navegador colapsa los saltos: sin esto, pegar diez nº de
  // carga los deja amontonados en un término que no casa con nada.
  function pegar(s: SeccionLista, e: React.ClipboardEvent<HTMLInputElement>) {
    const partes = partirTerminos(e.clipboardData.getData('text'))
    if (partes.length < 2) return
    e.preventDefault()
    const nuevos = resolverPegado(partes, s.opciones, s.seleccion, !!s.permitirLibre)
    if (nuevos.length > 0) s.onSeleccion([...s.seleccion, ...nuevos])
    setTexto('')
  }

  function teclas(s: SeccionLista, e: React.KeyboardEvent) {
    if (e.key !== 'Enter') return
    e.preventDefault()
    const primera = visibles[0]
    if (primera) { alternar(s, primera.valor); setTexto(''); return }
    const libre = texto.trim()
    if (s.permitirLibre && libre) { alternar(s, libre); setTexto('') }
  }

  const sep = dark ? 'border-white/8' : 'border-slate-200'
  const tono = activos > 0
    ? (dark
        ? 'bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/30'
        : 'bg-sky-50 text-sky-700 ring-1 ring-sky-200')
    : abierto
      ? (dark ? 'bg-white/10 text-slate-200' : 'bg-slate-200/70 text-slate-600')
      : (dark
          ? 'text-slate-500 hover:bg-white/[0.07] hover:text-slate-300'
          : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600')

  return (
    <>
      <button
        ref={boton}
        type="button"
        onClick={() => setAbierto(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={abierto}
        title={activos > 0 ? t('common.multiFiltro.filtrada', { campo: etiqueta }) : etiqueta}
        className={`inline-flex items-center gap-1 max-w-full h-[20px] px-1 -mx-1 rounded-md transition-colors duration-150 cursor-pointer ${tono}`}
      >
        <span className="truncate min-w-0">{etiqueta}</span>
        {activos > 0 ? (
          <span className={`shrink-0 min-w-[15px] h-[15px] px-1 rounded-full text-[9px] font-bold leading-[15px] text-center tabular-nums ${
            dark ? 'bg-sky-400 text-slate-900' : 'bg-sky-600 text-white'
          }`}>
            {activos}
          </span>
        ) : (
          <IcoEmbudo />
        )}
      </button>

      {abierto && pos && createPortal(
        <div
          ref={panel}
          style={{
            top: pos.top,
            left: pos.left,
            right: pos.right,
            minWidth: Math.max(pos.minWidth, 190),
            maxWidth: pos.maxWidth,
            maxHeight: pos.maxHeight,
          }}
          className={`fixed z-[61] flex flex-col overflow-hidden rounded-xl border shadow-xl ${
            dark ? 'border-white/10 bg-slate-900' : 'border-slate-200 bg-white'
          }`}
        >
          {/* Pestañas: sólo cuando la columna ofrece más de una cosa. */}
          {filtro.length > 1 && (
            <div className={`flex gap-1 p-1.5 border-b shrink-0 ${sep}`}>
              {filtro.map((s, i) => {
                const n = activosDe([s])
                return (
                  <button
                    key={s.nombre ?? i}
                    type="button"
                    onClick={() => { setPestana(i); setTexto('') }}
                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold transition-colors cursor-pointer ${
                      i === indice
                        ? (dark ? 'bg-sky-500/15 text-sky-300' : 'bg-sky-50 text-sky-700')
                        : (dark ? 'text-slate-400 hover:bg-white/5' : 'text-slate-500 hover:bg-slate-100')
                    }`}
                  >
                    {s.nombre}
                    {n > 0 && (
                      <span className={`tabular-nums text-[10px] font-bold ${dark ? 'text-sky-400' : 'text-sky-600'}`}>
                        {n}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          )}

          {/* Con una sola sección no hay pestañas, pero sí puede haber nombre:
              el embudo de «Estado» ofrece paletizada, y sin rotularlo el panel
              enseña «Paletizada / No paletizada / No consta» bajo una cabecera
              que dice otra cosa. */}
          {filtro.length === 1 && seccion.nombre && (
            <div className={`px-2.5 pt-2 pb-1 shrink-0 text-[10px] font-bold uppercase tracking-wider ${
              dark ? 'text-slate-500' : 'text-slate-400'
            }`}>
              {seccion.nombre}
            </div>
          )}

          {seccion.tipo === 'rango' ? (
            <div className="p-3 overflow-y-auto scroll-thin">
              <CamposRango
                dark={dark}
                desde={seccion.desde}
                hasta={seccion.hasta}
                onDesde={seccion.onDesde}
                onHasta={seccion.onHasta}
              />
            </div>
          ) : (
            <>
              {seccion.opciones.length >= UMBRAL_BUSCADOR && (
                <div className={`flex items-center gap-1.5 px-2.5 py-1.5 border-b shrink-0 ${sep} ${
                  dark ? 'text-slate-500' : 'text-slate-400'
                }`}>
                  <IcoLupa />
                  <input
                    autoFocus
                    autoComplete="off"
                    value={texto}
                    placeholder={t('common.multiFiltro.buscar')}
                    onChange={e => setTexto(e.target.value)}
                    onKeyDown={e => teclas(seccion, e)}
                    onPaste={e => pegar(seccion, e)}
                    className={`flex-1 min-w-0 bg-transparent outline-none text-[12px] ${
                      dark ? 'text-slate-100 placeholder-slate-600' : 'text-slate-800 placeholder-slate-400'
                    }`}
                  />
                </div>
              )}

              <ul
                role="listbox"
                aria-multiselectable={!seccion.unico}
                className="flex-1 overflow-y-auto scroll-thin py-1"
              >
                {visibles.map(o => {
                  const elegida = seccion.seleccion.includes(o.valor)
                  return (
                    <li key={o.valor} role="option" aria-selected={elegida}>
                      <button
                        type="button"
                        title={o.detalle ? `${o.titulo} · ${o.detalle}` : o.titulo}
                        onClick={() => alternar(seccion, o.valor)}
                        className={`w-full flex items-baseline gap-2 px-2.5 py-1.5 text-left cursor-pointer transition-colors duration-150 ${
                          elegida
                            ? (dark ? 'bg-sky-500/10' : 'bg-sky-50/70')
                            : (dark ? 'hover:bg-white/5' : 'hover:bg-slate-50')
                        }`}
                      >
                        <span className={`w-3.5 shrink-0 self-center ${
                          elegida ? (dark ? 'text-sky-400' : 'text-sky-600') : 'opacity-0'
                        }`}>
                          <IcoCheck />
                        </span>
                        <span className={`text-[12px] font-semibold shrink-0 ${
                          seccion.titulosMono === false ? '' : 'font-mono'
                        } ${dark ? 'text-slate-100' : 'text-slate-900'}`}>
                          {o.titulo}
                        </span>
                        {o.detalle && (
                          <span className={`text-[11px] truncate ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
                            {o.detalle}
                          </span>
                        )}
                      </button>
                    </li>
                  )
                })}

                {visibles.length === 0 && (
                  <li className={`px-2.5 py-3 text-[11px] text-center ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
                    {t('common.multiFiltro.sinResultados')}
                  </li>
                )}

                {ocultas > 0 && (
                  <li className={`px-2.5 py-1.5 text-[11px] border-t ${
                    dark ? 'border-white/8 text-slate-500' : 'border-slate-200 text-slate-400'
                  }`}>
                    {t('common.multiFiltro.masOpciones', { count: ocultas })}
                  </li>
                )}
              </ul>
            </>
          )}

          {activos > 0 && (
            <div className={`flex items-center justify-between gap-2 px-2.5 py-1.5 border-t shrink-0 ${sep}`}>
              <span className={`text-[11px] tabular-nums ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
                {t('common.multiFiltro.elegidos', { count: activos })}
              </span>
              <button
                type="button"
                onClick={() => limpiarFiltro(filtro)}
                className={`text-[11px] font-semibold transition-colors cursor-pointer ${
                  dark ? 'text-rose-400 hover:text-rose-300' : 'text-rose-500 hover:text-rose-600'
                }`}
              >
                {t('common.multiFiltro.limpiar')}
              </button>
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  )
}
